import React from 'react';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Text } from '@uhg-abyss/web/ui/Text';
import { NumberInput } from '@uhg-abyss/web/ui/NumberInput';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { styled } from '@uhg-abyss/web/tools/styled';
import { SharedDialog } from '../../../common/SharedDialog';
import { assignmentApi } from './api/assignmentApi';
import type { QuestionAssignmentSummary, SurveySummary } from './types';

interface SurveyAssignmentDialogProps {
  questionId: number;
  questionTitle: string;
  isOpen: boolean;
  onClose: () => void;
}

const FormRow = styled('div', {
  display: 'grid',
  gap: '0.25rem',
});

const INPUT_LABEL_COLOR = '$web.semantic.color.text.content.primary';

const selectInputCss = {
  'abyss-select-input-root': {
    width: '100%',
    minWidth: '12rem',
  },
  'abyss-select-input-label': {
    color: INPUT_LABEL_COLOR,
    fontWeight: 600,
    lineHeight: 1.2,
  },
  'abyss-select-input-container': {
    minHeight: '2.5rem',
    borderRadius: '8px',
    border: '1px solid #b6c9d9',
    boxSizing: 'border-box' as const,
  },
  'abyss-select-input-selection-container': {
    minHeight: '2.5rem',
    padding: '0 0.75rem',
    fontSize: '0.95rem',
  },
} as const;

const numberLabelCss = {
  'abyss-number-input-label': {
    fontSize: '0.8rem',
    fontWeight: 700,
    color: '#1a3a5c',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
} as const;

const AssignmentList = styled('div', {
  display: 'grid',
  gap: '0.25rem',
  padding: '0.5rem',
  backgroundColor: '#f5f9fc',
  borderRadius: '8px',
  border: '1px solid #c8d8e6',
  maxHeight: '160px',
  overflowY: 'auto',
});

const AssignmentRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '0.5rem',
  padding: '0.2rem 0',
});

const ErrorText = styled(Text, {
  color: '#c0392b',
  margin: 0,
  fontSize: '0.85rem',
});

export const SurveyAssignmentDialog = ({
  questionId,
  questionTitle,
  isOpen,
  onClose,
}: SurveyAssignmentDialogProps) => {
  const [selectedSurveyId, setSelectedSurveyId] = React.useState<number | ''>('');
  const [orderId, setOrderId] = React.useState('');
  const [surveys, setSurveys] = React.useState<SurveySummary[]>([]);
  const [currentAssignments, setCurrentAssignments] = React.useState<QuestionAssignmentSummary[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isAssigning, setIsAssigning] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    Promise.all([
      assignmentApi.listSurveys(),
      assignmentApi.listByQuestion(questionId),
    ])
      .then(([surveyResponse, assignmentResponse]) => {
        if (cancelled) {
          return;
        }
        setSurveys(surveyResponse.surveys);
        setCurrentAssignments(assignmentResponse.assignments);
      })
      .catch((err) => {
        if (!cancelled) {
          setError((err as Error).message ?? 'Failed to load assignment details');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, questionId]);

  const alreadyAssigned =
    selectedSurveyId
    && currentAssignments.some((a) => {return a.surveyId === selectedSurveyId});

  const refresh = React.useCallback(async () => {
    const assignmentResponse = await assignmentApi.listByQuestion(questionId);
    setCurrentAssignments(assignmentResponse.assignments);
  }, [questionId]);

  const handleAssign = async () => {
    if (!selectedSurveyId) return;
    setIsAssigning(true);
    setError(null);
    try {
      const order = orderId ? parseInt(orderId, 10) : undefined;
      await assignmentApi.assign(selectedSurveyId, questionId, order);
      setOrderId('');
      await refresh();
    } catch (err) {
      setError((err as Error).message ?? 'Assignment failed');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleUnassign = async (surveyId: number) => {
    setError(null);
    try {
      await assignmentApi.deassign(surveyId, questionId);
      await refresh();
    } catch (err) {
      setError((err as Error).message ?? 'Removal failed');
    }
  };

  return (
    <SharedDialog
      ariaLabel="Survey assignments"
      isOpen={isOpen}
      onClose={onClose}
      mode="app"
      title="Survey assignments"
      description={<strong>{questionTitle}</strong>}
      size="md"
      secondaryAction={{ label: 'Close', variant: 'outline', onClick: onClose }}
      primaryAction={{
        label: alreadyAssigned ? 'Already assigned' : isAssigning ? 'Assigning…' : 'Assign',
        disabled: !selectedSurveyId || !!alreadyAssigned || isAssigning || isLoading,
        onClick: handleAssign,
      }}
    >
        <FormRow>
          <SelectInput
            label="Survey"
            id="assign-survey-select"
            value={selectedSurveyId}
            onChange={(value) => {return setSelectedSurveyId(value ? Number(value) : '')}}
            options={surveys.filter((survey) => {return survey.active}).map((survey) => {return {
              label: survey.name,
              value: survey.surveyId,
            }})}
            placeholder="Select a survey"
            isClearable
            css={selectInputCss}
          />
        </FormRow>

        <FormRow>
          <NumberInput
            label="Display order"
            value={orderId}
            onChange={setOrderId}
            minValue={1}
            subText="Leave blank to append automatically"
            css={numberLabelCss}
          />
        </FormRow>

        {isLoading ? (
          <Text style={{ margin: 0, color: '#666' }}>Loading assignment details…</Text>
        ) : currentAssignments.length > 0 ? (
          <React.Fragment>
            <Text style={{ margin: 0, fontSize: '0.8rem', fontWeight: 700, color: '#1a3a5c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Assigned surveys
            </Text>
            <AssignmentList>
              {currentAssignments.map((a) => {return (
                <AssignmentRow key={a.assignmentId}>
                  <Text style={{ margin: 0, fontSize: '0.85rem' }}>
                    {a.surveyName}
                    {a.orderId !== null ? ` (order ${a.orderId})` : ''}
                  </Text>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {return handleUnassign(a.surveyId)}}
                  >
                    Remove
                  </Button>
                </AssignmentRow>
              )})}
            </AssignmentList>
          </React.Fragment>
        ) : (
          <Text style={{ margin: 0, color: '#666' }}>This question is not assigned to any surveys yet.</Text>
        )}

        {error && <ErrorText>{error}</ErrorText>}
    </SharedDialog>
  );
};
